/**
 * ReturnToOneStop — the "Back to One Stop" strip on a journey page that was
 * launched FROM a One Stop page (KP, 2026-09-17).
 *
 * WHEN IT SHOWS. Only when the URL carries `from=onestop`. A journey opened from
 * a link, a QR code, a kiosk or a search result carries no such parameter and
 * renders nothing here — the same published page serves both arrivals, and this
 * is how it tells them apart. The One Stop hand-off adds `return=<its own URL>`
 * and `fromLabel=<its page name>` alongside; both are optional.
 *
 * WHERE IT SITS. A slim full-width strip ABOVE the journey card — above the
 * brand header and the progress indicator — on every page of the journey,
 * including the confirmation. Outside the brand-themed card on purpose: it is
 * "you came from somewhere", not a step control, and it must never compete
 * with the step's own primary button. Sticky, so it survives a long slot
 * picker or form on a phone. 44px on touch, 40px otherwise.
 *
 * WHAT IT DOES. The journey tab was opened by script from the One Stop tab, so
 * the browser lets this page close ITSELF (`window.close()` is permitted for a
 * script-opened browsing context regardless of the severed opener) — the
 * student lands back on the One Stop tab with the board exactly as they left
 * it, no tab switching. When the browser refuses (a direct link, a kiosk, a
 * same-tab hand-off), the same control navigates to the `return` URL instead.
 * Registered clean-ups (an unfinished appointment hold) run first, so leaving
 * mid-flow does not lock a slot for five minutes.
 *
 * COLOUR. Subtle and brand-appropriate: a 6% tint of the journey's brand colour
 * over white, a hairline in the surface border, body-colour text, brand-colour
 * chevron. On the confirmation page the brand colour is used a little more
 * strongly for the "Done" affordance — there is nothing left to compete with.
 *
 * SECURITY. `return` is navigated to only when it is an absolute `https:` URL
 * with no embedded credentials — it arrives on the wire and goes into
 * `location.assign`, so `javascript:` and `data:` must never reach it. The
 * label is rendered as text, never as markup.
 */
import React from "react";

export const ONE_STOP_RETURN_PARAMS = {
  source: "from",
  sourceValue: "onestop",
  returnUrl: "return",
  fromLabel: "fromLabel",
} as const;

export interface OneStopReturn {
  readonly returnUrl: string | null;
  readonly fromLabel: string;
}

function safeHttpsUrl(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/** `null` unless this page was reached from a One Stop page. */
export function readOneStopReturn(search?: string): OneStopReturn | null {
  if (typeof window === "undefined" && search === undefined) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search !== undefined ? search : window.location.search);
  } catch {
    return null;
  }
  if (params.get(ONE_STOP_RETURN_PARAMS.source) !== ONE_STOP_RETURN_PARAMS.sourceValue) return null;
  const label = (params.get(ONE_STOP_RETURN_PARAMS.fromLabel) || "").trim().slice(0, 60);
  return { returnUrl: safeHttpsUrl(params.get(ONE_STOP_RETURN_PARAMS.returnUrl)), fromLabel: label };
}

/* ── clean-ups a journey wants run before the student leaves mid-flow ─────── */

type Cleanup = () => Promise<unknown> | unknown;
const cleanups = new Set<Cleanup>();

/**
 * A journey that holds something the student would otherwise leave behind (an
 * appointment hold) registers a release here; the strip runs every registered
 * clean-up before closing or navigating. Returns the unregister function — call
 * it when the hold is spent or released by the flow itself.
 */
export function registerReturnCleanup(fn: Cleanup): () => void {
  cleanups.add(fn);
  return () => { cleanups.delete(fn); };
}

async function runCleanups(): Promise<void> {
  const pending = [...cleanups];
  cleanups.clear();
  await Promise.allSettled(pending.map((fn) => Promise.resolve().then(fn)));
}

/* ── is the booking complete? ────────────────────────────────────────────────────── */

/**
 * KP, 2026-09-17: leaving before the booking is complete loses it, so the strip
 * WARNS first. The journey reports where it stands — `setJourneyComplete(true)`
 * on its confirmation / ticket screen, `false` on every other step. Unknown
 * (nothing reported yet) counts as NOT complete: a warning the student can wave
 * through is cheaper than a lost booking.
 */
let complete = false;
const completeListeners = new Set<(v: boolean) => void>();
export function setJourneyComplete(v: boolean): void {
  if (complete === v) return;
  complete = v;
  completeListeners.forEach((fn) => fn(v));
}
export const isJourneyComplete = (): boolean => complete;
function useJourneyComplete(): boolean {
  const [v, setV] = React.useState(complete);
  React.useEffect(() => { completeListeners.add(setV); return () => { completeListeners.delete(setV); }; }, []);
  return v;
}

/**
 * Leave the journey and return to One Stop. Exported so a journey's own
 * confirmation "Done" can do exactly what the strip does.
 */
export async function returnToOneStop(ret: OneStopReturn): Promise<void> {
  await runCleanups();
  // Closing is permitted for a script-opened tab; it is a no-op otherwise.
  try { window.close(); } catch { /* not closable */ }
  // Still here → the browser declined. Navigate instead, when we have a safe URL.
  window.setTimeout(() => {
    if (ret.returnUrl) window.location.assign(ret.returnUrl);
  }, 60);
}

/* ── the strip ────────────────────────────────────────────────────────────── */

export function ReturnToOneStopBar({ tint, done, testid, preview }: {
  /** The journey's brand colour (hex). Falls back to the jrni primary token. */
  readonly tint?: string | null;
  /** True on a confirmation page: the flow is complete, the control reads "Done". */
  readonly done?: boolean;
  readonly testid?: string;
  /** The editor's own preview: draws the strip for this label without reading the
   *  URL, and the control is inert. Never set on a public page. */
  readonly preview?: { readonly fromLabel: string } | null;
}): React.JSX.Element | null {
  const fromUrl = React.useMemo(() => readOneStopReturn(), []);
  const ret: OneStopReturn | null = preview ? { returnUrl: null, fromLabel: preview.fromLabel } : fromUrl;
  const [busy, setBusy] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const finished = useJourneyComplete() || !!done;
  if (!ret) return null;
  const brand = tint && /^#[0-9a-f]{6}$/i.test(tint) ? tint : "var(--jrni-color-primary-1, #4F00F2)";
  const where = ret.fromLabel || "One Stop";
  const label = finished ? `Done — back to ${where}` : `Back to ${where}`;
  const leave = () => {
    setConfirming(false);
    setBusy(true);
    void returnToOneStop(ret).finally(() => setBusy(false));
  };
  const go = () => {
    if (busy || preview) return;
    // Not finished → warn first; the booking is lost on the way out.
    if (!finished) { setConfirming(true); return; }
    leave();
  };
  return (
    <div
      data-testid={testid || "return-to-onestop"}
      data-onestop-return=""
      style={{
        position: preview ? "relative" : "sticky", top: 0, zIndex: 30,
        display: "flex", alignItems: "center", gap: 12,
        // ALIGNED TO THE JOURNEY'S CONTENT EDGE, not to a centred column. All three
        // hosts (appointment, event, walk-in) draw their body text 37px from the
        // left at every width: a 12px inset around the card (the Designer's
        // scroller on appointment and event, the page wrapper on walk-in) + 1px
        // card border + 24px body padding. 35px here + the button's own 2px is that
        // edge. The old rule centred the strip on an assumed 1120px column the
        // journeys never had, so the link drifted with the viewport.
        //
        // The editor's own preview sits over a mock header whose text is 16px in,
        // not over a real journey, so it keeps the narrow inset (14 + 2).
        minHeight: 40, padding: preview ? "0 12px 0 14px" : "0 12px 0 35px",
        boxSizing: "border-box",
        background: `color-mix(in srgb, ${brand} 6%, var(--jrni-color-base-white, #FFFFFF))`,
        borderBottom: "1px solid var(--jrni-color-surface-border, #E6E8EC)",
        fontFamily: "inherit",
      }}
    >
      <button
        type="button"
        onClick={go}
        disabled={busy}
        aria-label={label}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          minHeight: 40, padding: "0 6px 0 2px", margin: 0,
          border: 0, borderRadius: 8, background: "transparent",
          color: finished ? brand : "var(--jrni-color-text-body, #333333)",
          fontFamily: "inherit", fontSize: 13, fontWeight: finished ? 700 : 600, lineHeight: 1,
          cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* The chevron is drawn 6px inside its 16px box (the path starts at x=9 of
            24), less half its stroke: the INK begins 5.27px in. Aligning the box
            to the content edge left the arrow visibly indented, so the box is
            pulled back by that much and the ink - what the eye lines up - lands
            on the edge. */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={brand} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginLeft: -5 }}>
          <path d="M15 6l-6 6 6 6" />
        </svg>
        <span>{busy ? "Returning…" : label}</span>
      </button>
      {confirming ? (
        <div role="presentation" onClick={() => setConfirming(false)} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(15,16,22,.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="onestop-leave-title" aria-describedby="onestop-leave-body" onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 400, background: "var(--jrni-color-base-white, #FFFFFF)", borderRadius: 12, boxShadow: "0 18px 48px rgba(15,16,22,.28)", padding: "20px 20px 16px", boxSizing: "border-box", fontFamily: "inherit" }}>
            <div id="onestop-leave-title" style={{ fontSize: 16, fontWeight: 700, color: "var(--jrni-color-text-strong, #1B1D24)", lineHeight: 1.3 }}>Your booking isn’t finished</div>
            <div id="onestop-leave-body" style={{ fontSize: 13.5, color: "var(--jrni-color-text-body, #333333)", lineHeight: 1.5, marginTop: 8 }}>
              If you go back to {where} now, what you’ve entered here is lost and nothing is booked. You can come back and start again.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 18 }}>
              <button type="button" autoFocus onClick={() => setConfirming(false)}
                style={{ minHeight: 40, padding: "0 16px", borderRadius: 8, border: "1px solid var(--jrni-color-surface-border, #D1D4DC)", background: "transparent", color: "var(--jrni-color-text-body, #333333)", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                Keep booking
              </button>
              <button type="button" onClick={leave}
                style={{ minHeight: 40, padding: "0 16px", borderRadius: 8, border: 0, background: brand, color: "#FFFFFF", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                Leave without booking
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
